/**
 * NPC daily life (lane C0-NPC, N1; data/npcLife.ts, research docs/research/NPC_LIFE.md): the people of a zone who
 * live by five needs over a default plan. Each person picks the act that suits them best now (plan × needs × ties ×
 * distance), walks to its place and does it; the act is shown through the replicated emote channel
 * (`e.emote = 'life-<act>'`). Sleeping people are indoors: their entity leaves the world and comes back at the door.
 *
 * Level of detail: within `LIFE.nearR` of the player people think every tick and walk real paths (at most
 * `LIFE.pathsPerTick` searches a tick); farther away they think once a second and travel in a straight line, so a
 * town of forty costs next to nothing. Rumours of what the player did pass between people at social acts and are told
 * to the player, first-hand or heard.
 *
 * Determinism: no DOM, timers or `Math.random`. The draws come from a stream seeded by the map's seed (itself from
 * the World's seed), so the town's life never shifts the World's own draws — every other rule comes out as before —
 * and the same seed lives the same day on every machine. Time only moves through `step(dt)`.
 */
import { QUEST_CLEAR_R, QUEST_KEEP_CLEAR } from '../data/plainsFill';
import { plains256 } from '../data/zones';
import { DAY_SECONDS, dayPartAt, FOLK, type DayPart } from '../data/folk';
import { BUILDINGS, LIFE_STATIONS, PLAINS_SIZE, type XY } from '../data/layouts/plains';
import {
  canDo,
  CHAT_LINES,
  LIFE,
  LIFE_ACT_IDS,
  LIFE_BOATS,
  LIFE_ACTS,
  LIFE_NEEDS,
  LIFE_PLACES,
  LIFE_TIES,
  lifeEmote,
  lifePeopleOf,
  MARTIAL_JOBS,
  planOf,
  RIVAL_LINES,
  RUMOUR,
  RUMOUR_LINES,
  SMALL_HOMES,
  TIE_LINES,
  WORK_LINES,
  type LifeActDef,
  type LifeActId,
  type LifeNeed,
  type LifePerson,
  type LifePlace,
  type PlaceSource,
  type RumourKind,
  type TieKind,
} from '../data/npcLife';
import { isOpen, Tile, tileAt, type GameMap, type Point } from '../map';
import { findPath, freeAt, nearestFloor, type Waypoint } from '../path';
import { mulberry32, type Rng } from '../rng';
import type { Entity, PlayerCommand, World, WorldEvent } from '../world';
import { ACT_VOICE, type TalkOn } from '../data/npcTalk';
import { getSchool } from '../data/schools';
import { rankFor, STAGES } from '../cultivation';
import { pkColor } from '../karma';
import { AFFINITY, affinityTier, GIFT_LINES, JOB_LIKES, TIER_LINES } from '../data/npcAffinity';
import { GOODS } from '../data/professions';
import { addAffinity, addScore, affinityOf, memoryKey, memoryOf, setMemoryKey, standingOf } from './npcMemory';
import { candidates, pickLine, type TalkContext } from './npcTalk';
import { LIFE_EVENT } from '../data/npcEvents';
import { LifeEvents, type LifeEventHost } from './npcLifeEvents';
import type { CommandHandlers, SystemHost, WorldSystem } from './types';

/** A place on this map where people do an act. */
export interface LifeSpot {
  readonly place: LifePlace;
  readonly x: number;
  readonly y: number;
  readonly r: number;
  readonly n: number;
  /** People at it or on their way. */
  occ: number;
}

export interface Rumour {
  readonly id: number;
  readonly kind: RumourKind;
  /** Whom it is about (a name), or '' (a villager). */
  readonly who: string;
  readonly t: number;
}

/** One person's mind: their needs, the act under way, where they are going, what they know. */
export interface LifeMind {
  readonly def: LifePerson;
  /** Out of doors; null while indoors (asleep). */
  e: Entity | null;
  /** Where they are when indoors (their door), else mirrored from the entity. */
  x: number;
  y: number;
  readonly needs: Record<LifeNeed, number>;
  act: LifeActId | null;
  /** Seconds left of the act (counted from arrival). */
  actT: number;
  spot: LifeSpot | null;
  tx: number;
  ty: number;
  walking: boolean;
  path: Waypoint[] | null;
  /** A search failed for this target: travel straight. */
  noPath: boolean;
  /** The path under way ends a leg short of the target (a long way, searched in legs). */
  leg: boolean;
  stuckT: number;
  farT: number;
  barkT: number;
  soundT: number;
  contactT: number;
  /** The children's ring (chase). */
  phase: number;
  /** Rumour id → hops from the one who saw it (0: saw it). */
  readonly knows: Map<number, number>;
  /** Rumours already told to the player. */
  readonly told: Set<number>;
  /** The part of the day the act was picked in: a new part starts the round again (the schedule). */
  part: DayPart;
  /** When they may greet the player again (World time). */
  greetAt: number;
  /** Seconds the player has sat with them at this meal, tea or fishing (affinity, N3). */
  withT: number;
  /** Fighting beside the player (a sworn friend answering a gang-up, N3): seconds left. */
  rallyT: number;
  swingT: number;
  /** In an event (N2): their own life waits (they fight, or lie hurt). */
  held: boolean;
  /** Hurt: resting at home until this World time. */
  injuredUntil: number;
  /** The injured this person has already called on (by id, with the time). */
  readonly visited: Map<string, number>;
  /** Fleeing a fight: seconds left. */
  fleeT: number;
  scaredT: number;
  readonly home: LifeSpot;
  readonly ties: { readonly id: string; readonly kind: TieKind }[];
}

/** A boat on a lane: moored at the landing, out on the water with a fisherman aboard. */
export interface LifeBoat {
  readonly e: Entity;
  readonly station: string;
  readonly lane: readonly Point[];
  readonly speed: number;
  /** The landing it serves (the `boat` place's spot). */
  readonly spot: LifeSpot | null;
  /** Metres along the lane. */
  s: number;
  dir: 1 | -1;
  rider: LifeMind | null;
}

const SYSTEMS = new WeakMap<World, NpcLifeSystem>();
/** The life system of a World (tests, tools). */
export const npcLifeOf = (w: World): NpcLifeSystem | undefined => SYSTEMS.get(w);

const HUR = LIFE.hourSeconds;
const isWater = (t: number) => t === Tile.Water;
const isPaddy = (t: number) => t === Tile.Paddy;
const isTree = (t: number) => t === Tile.Tree;

export class NpcLifeSystem implements WorldSystem {
  readonly name = 'npcLife';
  readonly minds: LifeMind[] = [];
  readonly boats: LifeBoat[] = [];
  readonly rumours: Rumour[] = [];
  private readonly byId = new Map<string, LifeMind>();
  private readonly byEntity = new Map<number, LifeMind>();
  private readonly spots = new Map<LifePlace, LifeSpot[]>();
  private rnd: Rng = () => 0.5;
  private nextRumour = 1;
  private lastSaved = -Infinity;
  /** Fighters this step (anyone striking or struck who is not a townsperson). */
  private readonly fighters: Entity[] = [];

  /** The fights of N2 (systems/npcLifeEvents.ts). */
  readonly events: LifeEvents;

  /** A good from the bag given to one of the plains' people (affinity, N3). */
  readonly commands: CommandHandlers = {
    npcGiftItem: (c) => this.giftItem(c.id, c.good),
  };

  constructor(private readonly h: SystemHost) {
    SYSTEMS.set(h.world, this);
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const self = this;
    const host: LifeEventHost = {
      h,
      rnd: () => self.rnd(),
      people: () =>
        self.minds.map((m) => ({ id: m.def.id, job: m.def.job, act: m.act, walking: m.walking, e: m.e, held: m.held })),
      hold: (id, on) => self.hold(id, on),
      injure: (id) => self.injure(id),
      tiesOf: (id) => self.byId.get(id)?.ties.filter((t) => t.kind !== 'rival').map((t) => t.id) ?? [],
      memoryKeyOf: (id) => `${h.world.map.zone}/life/${id}`,
      sendTo: (id, x, y) => self.sendTo(id, x, y),
      bark: (e, text) => self.bark(e, text),
      rumour: (who, x, y) => self.rumour('rescue', who, x, y),
    };
    this.events = new LifeEvents(host);
  }

  /** Holds a person's own life for an event, or lets it go on. */
  private hold(id: string, on: boolean): void {
    const m = this.byId.get(id);
    if (!m) return;
    m.held = on;
    m.walking = false;
    m.path = null;
    if (m.e) {
      m.e.moving = false;
      this.setEmote(m.e, null);
    }
    if (!on) this.decide(m);
  }

  /** Hurt: home to rest an in-game day (never dead); the family and friends will call. */
  private injure(id: string): void {
    const m = this.byId.get(id);
    if (!m) return;
    m.injuredUntil = this.h.world.time + LIFE_EVENT.injured;
    m.held = false;
    this.begin(m, 'recover', m.home, false);
  }

  /** Sends someone walking to a point (the teacher to the hurt disciple). */
  private sendTo(id: string, x: number, y: number): void {
    const m = this.byId.get(id);
    if (!m?.e) return;
    const f = freeAt(this.h.world.map, x, y) ? { x, y } : { x: m.e.x, y: m.e.y };
    m.tx = f.x;
    m.ty = f.y;
    m.walking = true;
    m.path = null;
    m.noPath = false;
    this.setEmote(m.e, null);
  }

  // ───────────────────────── setup ─────────────────────────

  /** Made on the first step, not in `setup`: everything the World makes as it starts keeps the ids it always had. */
  private made = false;

  setup(): void {
    this.made = false;
  }

  /** The zone's people at their places for the hour, and the places of this map. */
  private make(): void {
    this.made = true;
    const w = this.h.world;
    const m = w.map;
    const big = m.zone === 'plains' && m.w === PLAINS_SIZE;
    const people = lifePeopleOf(m.zone, !big);
    if (!people.length) return;
    this.rnd = mulberry32(((m.seed | 0) * 7919 + 0x5eed) >>> 0);
    for (const [place, sources] of Object.entries(LIFE_PLACES) as [Exclude<LifePlace, 'home'>, readonly PlaceSource[]][]) {
      const list: LifeSpot[] = [];
      for (const s of sources) {
        if (s.map === 'big' && !big) continue;
        if (s.map === 'small' && big) continue;
        const at = this.resolve(m, s, big);
        if (at) list.push({ place, x: at.x, y: at.y, r: s.r ?? 1, n: s.n ?? 2, occ: 0 });
      }
      if (list.length) this.spots.set(place, list);
    }
    // The 256 m plains' opening: life spots keep clear of the quest people (data/plainsFill.ts QUEST_KEEP_CLEAR).
    if (big && plains256()) {
      for (const [place, list] of this.spots) {
        const kept = list.filter((sp) => !QUEST_KEEP_CLEAR.some(([x, y]) => Math.hypot(sp.x - x, sp.y - y) < QUEST_CLEAR_R + sp.r));
        if (kept.length) this.spots.set(place, kept);
        else this.spots.delete(place);
      }
    }
    const homes = this.homes(m, big);
    if (!homes.length) return;
    for (const def of people) {
      const hp = homes[def.home % homes.length]!;
      const home: LifeSpot = { place: 'home', x: hp.x, y: hp.y, r: 1.2, n: 8, occ: 0 };
      const needs = {} as Record<LifeNeed, number>;
      for (const n of LIFE_NEEDS) needs[n] = 40 + this.rnd() * 50;
      const mind: LifeMind = {
        def,
        e: null,
        x: home.x,
        y: home.y,
        needs,
        act: null,
        actT: 0,
        spot: null,
        tx: home.x,
        ty: home.y,
        walking: false,
        path: null,
        noPath: false,
        leg: false,
        stuckT: 0,
        farT: this.rnd(),
        barkT: LIFE.barkEvery[0] * this.rnd(),
        soundT: 0,
        contactT: RUMOUR.contactEvery * this.rnd(),
        phase: this.rnd() * Math.PI * 2,
        knows: new Map(),
        told: new Set(),
        part: dayPartAt(w.hour),
        greetAt: 0,
        withT: 0,
        rallyT: 0,
        swingT: 0,
        held: false,
        injuredUntil: 0,
        visited: new Map(),
        fleeT: 0,
        scaredT: 0,
        home,
        ties: [],
      };
      this.minds.push(mind);
      this.byId.set(def.id, mind);
    }
    for (const [a, b, kind] of LIFE_TIES) {
      const ma = this.byId.get(a);
      const mb = this.byId.get(b);
      if (!ma || !mb) continue;
      ma.ties.push({ id: b, kind });
      mb.ties.push({ id: a, kind });
    }
    // The boats on the lanes (256 m map), moored at their landings.
    if (big)
      for (const st of LIFE_STATIONS) {
        const kind = LIFE_BOATS[st.id];
        if (st.act !== 'boat' || !st.lane || !kind) continue;
        const lane = st.lane.map(([x, y]) => ({ x: x + 0.5, y: y + 0.5 }));
        const landing = this.spots.get('boat') ?? [];
        const spot =
          landing.reduce<LifeSpot | null>(
            (b, sp) => (!b || Math.hypot(sp.x - st.at[0], sp.y - st.at[1]) < Math.hypot(b.x - st.at[0], b.y - st.at[1]) ? sp : b),
            null,
          ) ?? null;
        for (let k = 0; k < st.n; k++) {
          const e = this.h.add({ kind: 'ambient', x: lane[0]!.x, y: lane[0]!.y, name: '', title: '', sprite: kind.sprite, speed: kind.speed, radius: 0.1 });
          const b: LifeBoat = { e, station: st.id, lane, speed: kind.speed, spot, s: k * 3, dir: 1, rider: null };
          this.place(b);
          this.boats.push(b);
        }
      }
    // Already somewhere in their day, as if the town had been going on all along.
    for (const mind of this.minds) this.decide(mind, true);
  }

  /** A place source on this map (null when it is not there). */
  private resolve(m: GameMap, s: PlaceSource, big: boolean): Point | null {
    let x: number;
    let y: number;
    if ('big' in s) {
      if (!big) return null;
      [x, y] = s.big;
    } else if ('station' in s) {
      if (!big) return null;
      const st = LIFE_STATIONS.find((l) => l.id === s.station);
      if (!st) return null;
      [x, y] = st.at;
    } else if ('mark' in s) {
      const base = s.mark === 'plaza' ? m.plaza : m.landmarks?.[s.mark];
      if (!base) return null;
      x = base.x + (s.dx ?? 0);
      y = base.y + (s.dy ?? 0);
    } else {
      const base = s.from === 'plaza' ? m.plaza : m.landmarks?.[s.from];
      if (!base) return null;
      const want = s.nearTile === 'water' ? isWater : s.nearTile === 'paddy' ? isPaddy : isTree;
      const at = this.nearTile(m, base.x, base.y, s.within, want);
      if (!at) return null;
      [x, y] = at;
    }
    // Layout points name a tile's corner (data/layouts/plains.ts); the tile's middle is stood on.
    const tile = 'big' in s || 'station' in s || 'nearTile' in s ? 0.5 : 0;
    return this.floorNear(m, x + tile, y + tile, s.reach);
  }

  /** The nearest walkable tile beside a tile of a kind (paddies are walkable: the paddy itself). */
  private nearTile(m: GameMap, cx: number, cy: number, within: number, want: (t: number) => boolean): XY | null {
    const ci = Math.floor(cx);
    const cj = Math.floor(cy);
    let best: XY | null = null;
    let bd = Infinity;
    const R = Math.ceil(within);
    for (let j = cj - R; j <= cj + R; j++)
      for (let i = ci - R; i <= ci + R; i++) {
        if (i < 1 || j < 1 || i >= m.w - 1 || j >= m.h - 1) continue;
        const d = (i - ci) ** 2 + (j - cj) ** 2;
        if (d >= bd || d > within * within) continue;
        const t = tileAt(m, i, j);
        if (!isOpen(t)) continue;
        if (!(want(t) || want(tileAt(m, i + 1, j)) || want(tileAt(m, i - 1, j)) || want(tileAt(m, i, j + 1)) || want(tileAt(m, i, j - 1))))
          continue;
        best = [i, j];
        bd = d;
      }
    return best;
  }

  private floorNear(m: GameMap, x: number, y: number, reach = 4): Point | null {
    if (freeAt(m, x, y)) return { x, y };
    const a = nearestFloor(m, x, y, reach);
    return a ? { x: a[0], y: a[1] } : null;
  }

  /** The doors people live behind: the 256 m map's houses, else the 72 m map's house landmarks. */
  private homes(m: GameMap, big: boolean): Point[] {
    const out: Point[] = [];
    if (big) {
      for (const b of BUILDINGS) {
        if (b.kind !== 'house') continue;
        const [cx, cy, , hh] = b.rect;
        const p = this.floorNear(m, cx, cy + hh / 2 + 0.8);
        if (p) out.push(p);
      }
    } else
      for (const k of SMALL_HOMES) {
        const l = m.landmarks?.[k];
        const p = l ? this.floorNear(m, l.x, l.y + 0.4) : null;
        if (p) out.push(p);
      }
    return out;
  }

  // ───────────────────────── the tick ─────────────────────────

  step(dt: number): void {
    if (!this.made) this.make();
    if (!this.minds.length) return;
    const w = this.h.world;
    const p = w.player;
    this.fighters.length = 0;
    for (const o of w.entities.values())
      if (!o.dead && o.combatT > 0 && o.kind !== 'ambient' && o.kind !== 'npc') this.fighters.push(o);
    let paths: number = LIFE.pathsPerTick;
    for (const m of this.minds) {
      const near = Math.abs(m.x - p.x) < LIFE.nearR && Math.abs(m.y - p.y) < LIFE.nearR;
      if (near) {
        if (m.farT >= 0) this.wake(m);
        m.farT = -1;
        paths = this.think(m, dt, true, paths);
      } else {
        if (m.farT < 0) m.farT = 0;
        m.farT += dt;
        if (m.farT >= LIFE.farTick) {
          this.think(m, m.farT, false, 0);
          m.farT -= LIFE.farTick;
        }
      }
    }
    this.forget();
    for (const b of this.boats) this.row(b, dt);
    if (this.fighters.length) this.rally(dt);
    this.events.step(dt);
  }

  /** Coming within the near ring: off any wall a straight-line walk left them in, onto a real path. */
  private wake(m: LifeMind): void {
    const e = m.e;
    if (!e || this.riding(m)) return;
    const map = this.h.world.map;
    if (!freeAt(map, e.x, e.y, e.radius)) {
      const a = nearestFloor(map, e.x, e.y, 4);
      if (a) [e.x, e.y] = a;
      else [e.x, e.y] = [m.tx, m.ty];
      m.x = e.x;
      m.y = e.y;
    }
    m.path = null;
    m.noPath = false;
  }

  /** One person's thinking for `dt` seconds; returns the path searches left this tick. */
  private think(m: LifeMind, dt: number, near: boolean, paths: number): number {
    const a: LifeActDef | null = m.act ? LIFE_ACTS[m.act] : null;
    const doing = !!a && !m.walking;
    this.drain(m, dt, doing ? a : null);
    m.barkT -= dt;
    m.soundT -= dt;
    m.contactT -= dt;
    m.scaredT = Math.max(0, m.scaredT - dt);
    if (m.held) return paths;
    if (m.rallyT > 0 && m.e) {
      this.fightBeside(m, dt);
      return paths;
    }

    // Trouble close by: the civil run, the martial stand and watch.
    if (near && m.e && !this.riding(m) && (this.flee(m, dt) || m.fleeT > 0)) return paths;

    if (m.walking) {
      paths = this.walk(m, dt, near, paths);
      return paths;
    }
    m.actT -= dt;
    if (m.e && near) this.atAct(m, dt);
    if (doing && m.contactT <= 0 && a!.social) this.contact(m);
    if (m.actT <= 0 || this.broken(m)) this.decide(m);
    return paths;
  }

  /** The needs run down with the hours and fill up with the act under way. */
  private drain(m: LifeMind, dt: number, doing: LifeActDef | null): void {
    const k = dt / HUR;
    const martial = MARTIAL_JOBS.includes(m.def.job);
    const asleep = doing?.indoors;
    for (const n of LIFE_NEEDS) {
      if (n === 'train' && !martial) continue;
      let v = m.needs[n];
      if (!(n === 'rest' && asleep)) v -= LIFE.decay[n] * k;
      const g = doing?.gives[n];
      if (g) v += g * k;
      m.needs[n] = v < 0 ? 0 : v > 100 ? 100 : v;
    }
  }

  /** Whether the act under way has to stop: rain on a dry act, a critical need it does not meet. */
  private broken(m: LifeMind): boolean {
    if (!m.act) return true;
    const a: LifeActDef = LIFE_ACTS[m.act];
    const w = this.h.world;
    // Resting a wound: nothing but its end calls them out.
    if (m.act === 'recover') return m.injuredUntil <= w.time;
    // A new part of the day: the schedule's next round (dawn out of the door, night home).
    if (m.part !== dayPartAt(w.hour)) return true;
    if (a.dry && this.wet()) return true;
    if (a.parts && !a.parts.includes(dayPartAt(w.hour))) return true;
    for (const n of LIFE_NEEDS)
      if (m.needs[n] < LIFE.critical && !(a.gives[n]! > 0) && (n !== 'train' || MARTIAL_JOBS.includes(m.def.job))) return true;
    return false;
  }

  private wet(): boolean {
    const k = this.h.world.weather.kind;
    return k === 'rain' || k === 'storm' || k === 'snow';
  }

  // ───────────────────────── choosing ─────────────────────────

  /** The act that suits them best now, and its spot; `now` puts them straight there (setup). */
  private decide(m: LifeMind, now = false): void {
    const w = this.h.world;
    const part: DayPart = dayPartAt(w.hour);
    m.part = part;
    // Hurt: resting at home till it is over.
    if (m.injuredUntil > w.time) return this.begin(m, 'recover', m.home, now);
    // Family and friends call on the injured, once each, while awake.
    if (part !== 'night')
      for (const t of m.ties) {
        if (t.kind === 'rival') continue;
        const o = this.byId.get(t.id);
        if (!o || o.injuredUntil <= w.time || (m.visited.get(o.def.id) ?? -1) >= o.injuredUntil - LIFE_EVENT.injured) continue;
        if (this.rnd() < (t.kind === 'family' ? 0.8 : 0.4)) {
          m.visited.set(o.def.id, w.time);
          return this.begin(m, 'visitSick', o.home, now);
        }
      }
    const plan = planOf(m.def, part);
    const wet = this.wet();
    const martial = MARTIAL_JOBS.includes(m.def.job);
    let best: { id: LifeActId; spot: LifeSpot; s: number } | null = null;
    for (const id of LIFE_ACT_IDS) {
      if (!canDo(m.def, id)) continue;
      const a: LifeActDef = LIFE_ACTS[id];
      if (a.special) continue;
      if (a.parts && !a.parts.includes(part)) continue;
      if (a.dry && wet) continue;
      const pw = plan[id] ?? 0;
      let s = pw * LIFE.planK;
      for (const n of LIFE_NEEDS) {
        const g = a.gives[n];
        if (!g || (n === 'train' && !martial)) continue;
        const u = ((100 - m.needs[n]) / 100) ** 2 * LIFE.urgencyK;
        s += (u * g) / LIFE.giveScale;
      }
      // Nothing in the plan and nothing it gives that is wanted: not considered.
      if (s <= 0.05) continue;
      // Not the same act straight again (unless it is sleep, or the plan insists).
      if (id === m.act && !a.indoors) s -= 0.8;
      const spots = a.place === 'home' ? [m.home] : this.spots.get(a.place);
      if (!spots) continue;
      for (const sp of spots) {
        if (sp !== m.spot && sp.occ >= sp.n) continue;
        let v = s - Math.hypot(sp.x - m.x, sp.y - m.y) * LIFE.farCost + this.rnd() * LIFE.jitter;
        v += this.tieScore(m, sp);
        if (!best || v > best.s) best = { id, spot: sp, s: v };
      }
    }
    if (!best) {
      // Nowhere to be: home.
      best = { id: 'sleep', spot: m.home, s: 0 };
    }
    this.begin(m, best.id, best.spot, now);
  }

  /** Family, friends and the master pull toward a spot; a rival pushes away. */
  private tieScore(m: LifeMind, sp: LifeSpot): number {
    let v = 0;
    for (const t of m.ties) {
      const o = this.byId.get(t.id);
      if (!o || o.spot !== sp) continue;
      v += t.kind === 'rival' ? -LIFE.rivalAvoid : LIFE.tieWith;
    }
    return v;
  }

  /** Takes up an act: off to its spot (out of the door first), or there at once. */
  private begin(m: LifeMind, id: LifeActId, sp: LifeSpot, now: boolean): void {
    const w = this.h.world;
    const a: LifeActDef = LIFE_ACTS[id];
    this.disembark(m);
    if (m.spot) m.spot.occ--;
    m.spot = sp;
    sp.occ++;
    m.act = id;
    m.path = null;
    m.noPath = false;
    m.stuckT = 0;
    const ang = this.rnd() * Math.PI * 2;
    const k = sp.r * Math.sqrt(this.rnd());
    const want = { x: sp.x + Math.cos(ang) * k, y: sp.y + Math.sin(ang) * k };
    const at = freeAt(w.map, want.x, want.y) ? want : { x: sp.x, y: sp.y };
    m.tx = at.x;
    m.ty = at.y;
    if (m.e) this.setEmote(m.e, null);
    // Indoors already and staying in (asleep at home): nothing to walk.
    if (!m.e && a.indoors && sp === m.home) return this.arrive(m);
    if (!m.e) this.comeOut(m, now);
    if (now) {
      const e = m.e!;
      e.x = m.tx;
      e.y = m.ty;
      m.x = e.x;
      m.y = e.y;
      return this.arrive(m, true);
    }
    if (Math.hypot(m.tx - m.x, m.ty - m.y) < 0.4) return this.arrive(m);
    m.walking = true;
  }

  /** Out of the door: the entity comes back into the world. */
  private comeOut(m: LifeMind, now: boolean): void {
    const d = m.def;
    const x = now ? m.tx : m.home.x;
    const y = now ? m.ty : m.home.y;
    const e = this.h.add({
      kind: 'ambient',
      x,
      y,
      name: d.name,
      title: d.title,
      sprite: d.sprite,
      speed: d.child ? 2.2 : d.job === 'elder' ? 1.1 : 1.5,
      radius: d.child ? 0.2 : 0.28,
      facing: this.rnd() < 0.5 ? 1 : -1,
    });
    // Remembered by zone and id (npcMemory): standing, prices, gifts and the rumours' nudges.
    setMemoryKey(e, `${this.h.world.map.zone}/life/${d.id}`);
    m.e = e;
    m.x = x;
    m.y = y;
    this.byEntity.set(e.id, m);
  }

  /** At the act's spot: indoors for sleep, else the act's pose. */
  private arrive(m: LifeMind, now = false): void {
    const w = this.h.world;
    const a: LifeActDef = LIFE_ACTS[m.act!];
    m.walking = false;
    m.path = null;
    const [lo, hi] = a.dur;
    m.actT = lo + this.rnd() * (hi - lo);
    if (now) m.actT *= 0.3 + this.rnd() * 0.7;
    // Sleep runs to the morning: the decision comes again when the night is over (or a need wakes them).
    if (a.indoors) {
      const left = this.secondsToDawn();
      if (m.act === 'sleep' && left > m.actT) m.actT = left + this.rnd() * 30;
      this.goIn(m);
      return;
    }
    const e = m.e;
    if (!e) return;
    e.moving = false;
    this.setEmote(e, this.emoteFor(m));
    if (m.act === 'boatNet' && this.board(m)) this.setEmote(e, lifeEmote('boatNet'));
    else if (a.faceWater || m.act === 'boatNet') this.faceWater(e);
    else {
      const o = this.company(m)[0];
      if (o?.e) this.h.face(e, o.e);
    }
    m.soundT = 0.3 + this.rnd();
    // Greeting family and friends met at the spot (only heard near the player).
    const p = w.player;
    if (!now && Math.hypot(e.x - p.x, e.y - p.y) < LIFE.hearing && m.barkT < LIFE.barkEvery[0] * 0.6) {
      for (const t of m.ties) {
        const o = this.byId.get(t.id);
        if (!o?.e || o.spot !== m.spot || o.walking) continue;
        const pool = t.kind === 'rival' ? RIVAL_LINES : TIE_LINES[t.kind];
        this.bark(e, this.pick(pool));
        break;
      }
    }
  }

  private secondsToDawn(): number {
    const h = this.h.world.hour;
    const until = h < 5.5 ? 5.5 - h : 24 - h + 5.5;
    return until * (DAY_SECONDS / 24);
  }

  /** Behind the door: the entity leaves the world; what they know is shared with the family inside. */
  private goIn(m: LifeMind): void {
    const e = m.e;
    if (e) {
      this.setEmote(e, null);
      this.byEntity.delete(e.id);
      this.h.world.entities.delete(e.id);
      m.e = null;
    }
    m.x = m.home.x;
    m.y = m.home.y;
    for (const t of m.ties) {
      if (t.kind !== 'family') continue;
      const o = this.byId.get(t.id);
      if (!o || o.e || o.home !== m.home) continue;
      this.share(m, o);
      this.share(o, m);
    }
  }

  /** The emote an act shows (the hide-and-seek hiders crouch; the first at the spot seeks). */
  private emoteFor(m: LifeMind): string {
    if (m.act === 'hideSeek') {
      const first = this.company(m).every((o) => (o.e?.id ?? 0) > (m.e?.id ?? 0));
      return first ? lifeEmote('hideSeek') : 'life-hide';
    }
    return lifeEmote(m.act!);
  }

  private setEmote(e: Entity, id: string | null): void {
    if (e.emote === id) return;
    e.emote = id;
    // Pair acts start out of step (a spar is blows in turn).
    e.emoteT = id ? (e.id % 7) * 0.37 : 0;
  }

  // ───────────────────────── boats ─────────────────────────

  /** The boat at its place along the lane (and its rider aboard). */
  private place(b: LifeBoat): void {
    let s = b.s;
    for (let k = 0; k < b.lane.length - 1; k++) {
      const a = b.lane[k]!;
      const c = b.lane[k + 1]!;
      const l = Math.hypot(c.x - a.x, c.y - a.y);
      if (s <= l || k === b.lane.length - 2) {
        const t = l > 0 ? Math.min(1, s / l) : 0;
        const x = a.x + (c.x - a.x) * t;
        const y = a.y + (c.y - a.y) * t;
        b.e.dirX = (c.x - a.x) / (l || 1);
        b.e.dirY = (c.y - a.y) / (l || 1);
        b.e.x = x;
        b.e.y = y;
        const r = b.rider;
        if (r?.e) {
          r.e.x = x;
          r.e.y = y + 0.12;
          r.x = r.e.x;
          r.y = r.e.y;
        }
        return;
      }
      s -= l;
    }
  }

  private laneLength(b: LifeBoat): number {
    let n = 0;
    for (let k = 0; k < b.lane.length - 1; k++) n += Math.hypot(b.lane[k + 1]!.x - b.lane[k]!.x, b.lane[k + 1]!.y - b.lane[k]!.y);
    return n;
  }

  /** Out along the lane and back with a fisherman aboard; home to the landing without one. */
  private row(b: LifeBoat, dt: number): void {
    const r = b.rider;
    if (r && (r.act !== 'boatNet' || r.walking || !r.e)) b.rider = null;
    const L = this.laneLength(b);
    const was = b.s;
    if (b.rider) {
      b.s += b.dir * b.speed * dt;
      if (b.s > L) [b.s, b.dir] = [L, -1];
      if (b.s < 0) [b.s, b.dir] = [0, 1];
    } else if (b.s > 0) b.s = Math.max(0, b.s - b.speed * 1.5 * dt);
    b.e.moving = b.s !== was;
    if (b.s !== was || b.rider) this.place(b);
  }

  /** Aboard a free boat of this landing (true), else the net is cast from the bank. */
  private board(m: LifeMind): boolean {
    const b = this.boats.find((x) => x.spot === m.spot && !x.rider && x.s < 0.5);
    if (!b || !m.e) return false;
    b.rider = m;
    this.place(b);
    return true;
  }

  private riding(m: LifeMind): boolean {
    for (const b of this.boats) if (b.rider === m) return true;
    return false;
  }

  /** Off the boat at its landing, before walking anywhere. */
  private disembark(m: LifeMind): void {
    const b = this.boats.find((x) => x.rider === m);
    if (!b) return;
    b.rider = null;
    const sp = b.spot;
    if (m.e && sp) {
      m.e.x = sp.x;
      m.e.y = sp.y;
      m.x = sp.x;
      m.y = sp.y;
    }
  }

  // ───────────────────────── moving ─────────────────────────

  private walk(m: LifeMind, dt: number, near: boolean, paths: number): number {
    const e = m.e;
    if (!e) {
      this.arrive(m);
      return paths;
    }
    const left = Math.hypot(m.tx - e.x, m.ty - e.y);
    const sp = e.speed;
    if (!near) {
      // Far: a straight line, through anything (nobody sees it).
      const s = Math.min(left, sp * dt);
      if (left > 1e-3) {
        e.x += ((m.tx - e.x) / left) * s;
        e.y += ((m.ty - e.y) / left) * s;
      }
      e.moving = false;
      m.x = e.x;
      m.y = e.y;
      if (left - s < 0.3) this.arrive(m);
      return paths;
    }
    if (left < 0.3) {
      this.arrive(m);
      return paths;
    }
    if (!m.path && !m.noPath && paths > 0) {
      paths--;
      // A long way: searched a leg at a time (a bounded search each), the leg's end on the straight line there.
      let lx = m.tx;
      let ly = m.ty;
      m.leg = left > LIFE.legMax;
      if (m.leg) {
        const k = LIFE.legMax / left;
        const fl = nearestFloor(this.h.world.map, e.x + (m.tx - e.x) * k, e.y + (m.ty - e.y) * k, 4);
        if (fl) [lx, ly] = fl;
        else m.leg = false;
      }
      m.path = findPath(this.h.world.map, e.x, e.y, lx, ly);
      if (!m.path) m.noPath = true;
    }
    if (m.path) this.follow(m, e, dt);
    else if (m.noPath) {
      // No way round: straight on against the walls, and if stuck, there all the same.
      const s = Math.min(left, sp * dt);
      if (this.h.move(e, ((m.tx - e.x) / left) * s, ((m.ty - e.y) / left) * s)) {
        e.moving = true;
        m.stuckT = 0;
      } else if ((m.stuckT += dt) > 3) {
        e.x = m.tx;
        e.y = m.ty;
        this.arrive(m);
      }
    }
    m.x = e.x;
    m.y = e.y;
    return paths;
  }

  private follow(m: LifeMind, e: Entity, dt: number): void {
    const path = m.path!;
    const [px, py] = path[0]!;
    const dx = px - e.x;
    const dy = py - e.y;
    const d = Math.hypot(dx, dy);
    const s = e.speed * dt;
    if (d <= s) {
      if (freeAt(this.h.world.map, px, py, e.radius)) {
        e.x = px;
        e.y = py;
      }
      path.shift();
      m.stuckT = 0;
    } else if (!this.h.move(e, (dx / d) * s, (dy / d) * s)) {
      if ((m.stuckT += dt) > 0.3) {
        path.shift();
        m.stuckT = 0;
      }
    }
    if (!path.length) m.path = null;
    e.moving = true;
    // The end of a leg: the next is searched; the end of the whole way short of the spot: straight on.
    if (!m.path && !m.leg && Math.hypot(m.tx - e.x, m.ty - e.y) >= 0.3) m.noPath = true;
  }

  /** The act's own motion and voice while the player is near: the children's ring, a work sound, an aside. */
  private atAct(m: LifeMind, dt: number): void {
    const e = m.e!;
    const a: LifeActDef = LIFE_ACTS[m.act!];
    const w = this.h.world;
    const p = w.player;
    e.moving = false;
    if (a.move === 'circle' && m.spot) {
      const R = Math.min(2, m.spot.r);
      m.phase += (e.speed / R) * dt * 0.8;
      const tx = m.spot.x + Math.cos(m.phase) * R;
      const ty = m.spot.y + Math.sin(m.phase) * R;
      const d = Math.hypot(tx - e.x, ty - e.y);
      if (d > 0.05) {
        const s = Math.min(d, e.speed * dt);
        if (this.h.move(e, ((tx - e.x) / d) * s, ((ty - e.y) / d) * s)) e.moving = true;
      }
      m.x = e.x;
      m.y = e.y;
    } else if (!e.emote) this.setEmote(e, this.emoteFor(m));
    const d = Math.hypot(e.x - p.x, e.y - p.y);
    // The work's sound, beat by beat (an emote event the client's sounds pick up).
    if (a.sound && a.beat && m.soundT <= 0) {
      m.soundT = a.beat * (0.85 + this.rnd() * 0.3);
      if (d < LIFE.soundR && e.emote) this.h.emit({ type: 'emote', id: e.id, emote: e.emote });
    }
    if (m.barkT <= 0) {
      m.barkT = LIFE.barkEvery[0] + this.rnd() * (LIFE.barkEvery[1] - LIFE.barkEvery[0]);
      if (d < LIFE.hearing) {
        const pool = WORK_LINES[m.act!];
        const own = pool?.length && this.rnd() < 0.7 ? this.pick(pool) : this.voice(m, e, 'idle');
        if (own) this.bark(e, own);
      }
    }
    // A passer-by close enough to hear a rumour about themselves; else a greeting now and then.
    if (d < LIFE.tell && !p.dead) this.tellPlayer(m, e);
    if (d < FOLK.notice && !p.dead && w.time >= m.greetAt) {
      m.greetAt = w.time + FOLK.greetEvery;
      this.h.face(e, p);
      const tier = this.tierOf(m);
      const l = tier > 0 && this.rnd() < 0.5 ? this.tierLine(m, tier) : this.voice(m, e, 'greet');
      if (l) this.bark(e, l);
    }
    // Sitting with them at a meal, tea or the fishing: a little closer, once a day.
    if (d < AFFINITY.togetherR && !p.dead && !p.moving && (a.social || m.act === 'fishRod' || m.act === 'castNet')) {
      m.withT += dt;
      if (m.withT >= AFFINITY.togetherSeconds) {
        m.withT = -1e9;
        const key = memoryKey(w, e);
        const gd = memoryOf(w.progress).giftDay;
        const day = this.day();
        if (key && gd[`${key}#with`] !== day) {
          gd[`${key}#with`] = day;
          addAffinity(w, (x) => this.h.emit(x), key, AFFINITY.together, m.def.name);
          this.bark(e, m.act === 'fishRod' || m.act === 'castNet' ? 'นั่งตกปลาด้วยกันแบบนี้ สบายใจดีนะ' : 'กินด้วยกันอร่อยขึ้นเยอะเลย');
        }
      }
    } else if (m.withT > 0) m.withT = 0;
  }

  /** Today's key for the once-a-day rules (the progress's day, else the in-game day). */
  private day(): string {
    const w = this.h.world;
    return w.progress.arena?.day || `d${Math.floor(w.time / DAY_SECONDS)}`;
  }

  private keyOf(m: LifeMind): string {
    return `${this.h.world.map.zone}/life/${m.def.id}`;
  }

  /** How close this person is to the player (data/npcAffinity.ts tier 0–5). */
  tierOf(m: LifeMind): number {
    return affinityTier(affinityOf(this.h.world.progress, this.keyOf(m))).n;
  }

  private likesOf(m: LifeMind): { likes: readonly string[]; dislikes: readonly string[] } {
    return JOB_LIKES[m.def.job] ?? { likes: [], dislikes: [] };
  }

  private tierLine(m: LifeMind, tier: number): string | null {
    const pool = TIER_LINES[Math.min(5, tier)];
    if (!pool?.length) return null;
    const like = this.likesOf(m).likes[0];
    return this.pick(pool).replaceAll('{like}', like ? (GOODS[like]?.name ?? like) : 'ของกิน');
  }

  /** A good from the bag, once a day each: liked, plain or disliked by the person (data/npcAffinity.ts `JOB_LIKES`). */
  private giftItem(id: number, good: string): void {
    const w = this.h.world;
    const pr = w.progress;
    const e = w.get(id);
    const m = e ? this.byEntity.get(e.id) : undefined;
    if (!e || !m || w.player.dead) return;
    if (Math.hypot(e.x - w.player.x, e.y - w.player.y) > FOLK.notice + 1) return;
    const goods = pr.trades.goods;
    if (!GOODS[good] || !((goods[good] ?? 0) > 0)) return this.h.notice('ไม่มีของชิ้นนี้');
    const key = this.keyOf(m);
    const mem = memoryOf(pr);
    const day = this.day();
    if (mem.giftDay[key] === day) return this.bark(e, this.pick(GIFT_LINES.again));
    mem.giftDay[key] = day;
    goods[good] = goods[good]! - 1;
    if (goods[good] === 0) delete goods[good];
    const { likes, dislikes } = this.likesOf(m);
    const kind = likes.includes(good) ? 'liked' : dislikes.includes(good) ? 'disliked' : 'plain';
    const d = kind === 'liked' ? AFFINITY.giftLiked : kind === 'disliked' ? AFFINITY.giftDisliked : AFFINITY.giftPlain;
    addScore(mem.npc, key, kind === 'liked' ? 3 : kind === 'plain' ? 1 : -1);
    this.h.log('sys', `มอบ${GOODS[good]!.name}ให้${m.def.name}`);
    this.bark(e, this.pick(GIFT_LINES[kind]));
    addAffinity(w, (x) => this.h.emit(x), key, d, m.def.name);
    if (kind !== 'disliked') this.rumour('gift', m.def.name, m.x, m.y);
    this.h.emit({ type: 'progressChanged' });
  }

  /** A sworn friend (tier 4+, the martial) near a player set on by several foes comes to fight beside them. */
  private rally(dt: number): void {
    const w = this.h.world;
    const p = w.player;
    if (p.dead || w.duel) return;
    let foes = 0;
    for (const o of this.fighters)
      if (o.kind === 'monster' && o.targetId === p.id && Math.hypot(o.x - p.x, o.y - p.y) < 7) foes++;
    if (foes < AFFINITY.rallyFoes) return;
    for (const m of this.minds) {
      if (m.rallyT > 0 || m.held || !m.e || !MARTIAL_JOBS.includes(m.def.job) || this.riding(m)) continue;
      if (Math.hypot(m.x - p.x, m.y - p.y) > AFFINITY.rallyR || this.tierOf(m) < 4) continue;
      m.rallyT = 20;
      m.walking = false;
      m.path = null;
      this.setEmote(m.e, null);
      this.bark(m.e, this.pick(['สหาย! ข้ามาแล้ว!', 'ใครกล้ารังแกสหายข้า!']));
    }
    void dt;
  }

  /** Fighting beside the player: into reach of the nearest foe on them, a blow every so often. */
  private fightBeside(m: LifeMind, dt: number): void {
    const e = m.e!;
    const w = this.h.world;
    const p = w.player;
    m.rallyT -= dt;
    m.swingT -= dt;
    let foe: Entity | null = null;
    let bd = 12;
    for (const o of this.fighters) {
      if (o.kind !== 'monster' || o.dead || o.targetId !== p.id) continue;
      const d = Math.hypot(o.x - e.x, o.y - e.y);
      if (d < bd) [foe, bd] = [o, d];
    }
    if (!foe || m.rallyT <= 0) {
      m.rallyT = 0;
      e.combatT = 0;
      this.decide(m);
      return;
    }
    m.rallyT = Math.max(m.rallyT, 2);
    e.combatT = 2;
    this.h.face(e, foe);
    if (bd > 1.5) {
      const s = Math.min(bd - 1.2, e.speed * 1.4 * dt);
      e.moving = this.h.move(e, ((foe.x - e.x) / bd) * s, ((foe.y - e.y) / bd) * s);
    } else if (m.swingT <= 0) {
      m.swingT = 1.2;
      Object.assign(e, { atk: 30, hit: 10, crit: 0.05 });
      e.swingT = 0.32;
      this.h.emit({ type: 'basic', srcId: e.id, tgtId: foe.id, step: Math.floor(this.rnd() * 3) });
      this.h.hit(e, foe, 1, 'attack', 'basic');
    }
    m.x = e.x;
    m.y = e.y;
  }

  /** A line in the person's voice for the moment (data/npcTalk.ts, picked as npcTalk picks, from this system's draws). */
  private voice(m: LifeMind, e: Entity, on: TalkOn): string | null {
    const w = this.h.world;
    const pr = w.progress;
    const p = w.player;
    const q = w.currentQuest();
    const c: TalkContext = {
      role: ACT_VOICE[m.def.role],
      period: w.period.id,
      part: w.dayPart,
      weather: w.weather.kind,
      zone: w.map.zone,
      at: null,
      standing: standingOf(w, e),
      school: pr.school,
      karma: pr.karma,
      pk: pkColor(pr.pkPoints),
      hurt: !p.dead && p.hp < p.maxHp * 0.4,
      realm: Math.floor(rankFor(pr.cultivationSpent).index / STAGES),
      recent: new Set(),
      quest: q && pr.quest.status === 'active' ? q.id : null,
    };
    const l = pickLine(candidates(on, c), this.rnd);
    if (!l) return null;
    const school = pr.school ? getSchool(pr.school).name : '';
    return l.text.replaceAll('{school}', school);
  }

  /** Runs from a blow struck close by (the civil), or stands and watches it (the martial). */
  private flee(m: LifeMind, dt: number): boolean {
    const e = m.e!;
    m.fleeT = Math.max(0, m.fleeT - dt);
    let near: Entity | null = null;
    let bd: number = FOLK.danger;
    for (const o of this.fighters) {
      if (Math.abs(o.x - e.x) > bd || Math.abs(o.y - e.y) > bd) continue;
      const d = Math.hypot(o.x - e.x, o.y - e.y);
      if (d < bd) [near, bd] = [o, d];
    }
    if (!near) {
      if (m.fleeT <= 0) return false;
      e.moving = false;
      return true;
    }
    if (MARTIAL_JOBS.includes(m.def.job)) {
      this.h.face(e, near);
      return false;
    }
    if (m.scaredT <= 0 && Math.hypot(e.x - this.h.world.player.x, e.y - this.h.world.player.y) < LIFE.hearing) {
      this.bark(e, m.def.child ? 'แม่จ๋า! ช่วยด้วย!' : this.pick(['ช่วยด้วย! มีคนสู้กัน!', 'หนีเร็ว!', 'อย่ามาแถวนี้!']));
      m.scaredT = 8;
    }
    m.fleeT = 2.5;
    this.setEmote(e, null);
    const dx = e.x - near.x;
    const dy = e.y - near.y;
    const l = Math.hypot(dx, dy) || 1;
    const s = e.speed * 1.8 * dt;
    e.moving = this.h.move(e, (dx / l) * s, (dy / l) * s);
    e.sprinting = e.moving;
    m.x = e.x;
    m.y = e.y;
    // Back to the act's spot once it is over.
    m.walking = true;
    m.path = null;
    return true;
  }

  /** Turns toward the nearest water tile (fishing, washing). */
  private faceWater(e: Entity): void {
    const map = this.h.world.map;
    const ci = Math.floor(e.x);
    const cj = Math.floor(e.y);
    let best: Point | null = null;
    let bd = Infinity;
    for (let j = cj - 4; j <= cj + 4; j++)
      for (let i = ci - 4; i <= ci + 4; i++) {
        if (!isWater(tileAt(map, i, j))) continue;
        const d = (i + 0.5 - e.x) ** 2 + (j + 0.5 - e.y) ** 2;
        if (d < bd) [best, bd] = [{ x: i + 0.5, y: j + 0.5 }, d];
      }
    if (best) this.h.face(e, best);
  }

  /** Others at the same spot, at it (not on their way). */
  private company(m: LifeMind): LifeMind[] {
    const out: LifeMind[] = [];
    if (!m.spot || m.spot.place === 'home') return out;
    for (const o of this.minds) if (o !== m && o.spot === m.spot && !o.walking && o.e) out.push(o);
    return out;
  }

  // ───────────────────────── rumours ─────────────────────────

  /** Two at a social act: one passes a rumour on; with the player in hearing, said aloud now and then. */
  private contact(m: LifeMind): void {
    m.contactT = RUMOUR.contactEvery;
    const others = this.company(m);
    if (!others.length) return;
    const o = others[Math.floor(this.rnd() * others.length)]!;
    const fresh = [...m.knows.keys()].filter((id) => !o.knows.has(id));
    const w = this.h.world;
    const p = w.player;
    const hear = !!m.e && Math.hypot(m.e.x - p.x, m.e.y - p.y) < LIFE.hearing;
    if (fresh.length && this.rnd() < RUMOUR.passChance) {
      const id = fresh[Math.floor(this.rnd() * fresh.length)]!;
      this.learn(o, id, (m.knows.get(id) ?? 0) + 1);
      const r = this.rumours.find((x) => x.id === id);
      if (r && hear && m.barkT <= 0) {
        m.barkT = LIFE.barkEvery[0];
        this.bark(m.e!, this.fill(this.pick(RUMOUR_LINES[r.kind].gossip), r));
      }
      return;
    }
    // Small talk, now and then, for the player to overhear.
    if (hear && m.barkT <= 0 && o.e && this.rnd() < 0.35) {
      m.barkT = LIFE.barkEvery[0] + this.rnd() * 10;
      const [q, ans] = this.pick(CHAT_LINES);
      this.bark(m.e!, q);
      o.barkT = Math.max(o.barkT, 4);
      this.h.emit({ type: 'bark', id: o.e.id, text: ans });
    }
  }

  private share(from: LifeMind, to: LifeMind): void {
    for (const [id, hops] of from.knows) if (!to.knows.has(id)) this.learn(to, id, hops + 1);
  }

  /** Someone hears a rumour: they know it now, and think a little better (or worse) of the player. */
  private learn(m: LifeMind, id: number, hops: number): void {
    if (m.knows.has(id)) return;
    m.knows.set(id, hops);
    const r = this.rumours.find((x) => x.id === id);
    const w = this.h.world;
    if (!r || !m.e) return;
    const key = memoryKey(w, m.e);
    if (key) addScore(memoryOf(w.progress).npc, key, RUMOUR_LINES[r.kind].tone * RUMOUR.memory);
  }

  /** A rumour the player has not heard from this person yet, told once. */
  private tellPlayer(m: LifeMind, e: Entity): void {
    if (m.barkT > LIFE.barkEvery[1] - 3) return;
    for (const [id, hops] of m.knows) {
      if (m.told.has(id)) continue;
      const r = this.rumours.find((x) => x.id === id);
      m.told.add(id);
      if (!r) continue;
      const lines = RUMOUR_LINES[r.kind];
      // About themselves: thanks, not a rumour.
      const self = r.who === m.def.name;
      const text = self ? lines.seen[lines.seen.length - 1]! : this.pick(hops === 0 ? lines.seen : lines.heard);
      this.bark(e, this.fill(text, r, self));
      m.barkT = LIFE.barkEvery[1];
      return;
    }
  }

  private fill(text: string, r: Rumour, self = false): string {
    const who = self ? 'ข้า' : r.who || 'ชาวบ้าน';
    return text.replaceAll('{who}', who);
  }

  /** A new rumour, known at once to everyone out of doors within `RUMOUR.witness` of (x, y). */
  private rumour(kind: RumourKind, who: string, x: number, y: number): void {
    const w = this.h.world;
    const r: Rumour = { id: this.nextRumour++, kind, who, t: w.time };
    this.rumours.push(r);
    while (this.rumours.length > RUMOUR.max) this.dropRumour(this.rumours[0]!.id);
    let any = false;
    for (const m of this.minds) {
      if (!m.e || Math.hypot(m.x - x, m.y - y) > RUMOUR.witness) continue;
      this.learn(m, r.id, 0);
      // Whom it is about has nothing new to tell the player about it beyond thanks.
      any = true;
    }
    // Word gets about even with nobody watching: someone in town has it second-hand.
    if (!any) {
      const out = this.minds.filter((m) => !!m.e);
      const m = out.length ? out[Math.floor(this.rnd() * out.length)]! : null;
      if (m) this.learn(m, r.id, 1);
    }
  }

  private dropRumour(id: number): void {
    const k = this.rumours.findIndex((r) => r.id === id);
    if (k >= 0) this.rumours.splice(k, 1);
    for (const m of this.minds) {
      m.knows.delete(id);
      m.told.delete(id);
    }
  }

  private forget(): void {
    const now = this.h.world.time;
    while (this.rumours.length && now - this.rumours[0]!.t > RUMOUR.life) this.dropRumour(this.rumours[0]!.id);
  }

  observe(ev: WorldEvent): void {
    if (!this.minds.length && ev.type !== 'affinity') return;
    this.events.observe(ev);
    const w = this.h.world;
    const p = w.player;
    switch (ev.type) {
      case 'affinity': {
        const t = affinityTier(affinityOf(w.progress, ev.key));
        this.h.notice(`ความสนิทสนมกับ${ev.name}: ขั้น${t.name} · ${t.opens}`);
        const m = this.minds.find((x) => this.keyOf(x) === ev.key);
        if (!m?.e) return;
        const line = this.tierLine(m, ev.tier);
        if (line) this.bark(m.e, line);
        // The bond: their own rare gift, once.
        const gd = memoryOf(w.progress).giftDay;
        if (ev.tier >= 5 && gd[`${ev.key}#bond`] !== 'given') {
          gd[`${ev.key}#bond`] = 'given';
          const g = AFFINITY.bondGift;
          const goods = w.progress.trades.goods;
          goods[g.good] = (goods[g.good] ?? 0) + g.n;
          this.h.log('sys', `${m.def.name}มอบ${GOODS[g.good]?.name ?? g.good}ให้เป็นของขวัญประจำตัว`);
          this.h.emit({ type: 'progressChanged' });
        }
        return;
      }
      case 'death': {
        const mon = w.get(ev.id);
        if (!mon || mon.kind !== 'monster' || p.dead) return;
        if (mon.targetId !== p.id && Math.hypot(mon.x - p.x, mon.y - p.y) > 6) return;
        let saved: LifeMind | null = null;
        let bd = 9;
        for (const m of this.minds) {
          if (!m.e) continue;
          const d = Math.hypot(m.x - mon.x, m.y - mon.y);
          if (d < bd) [saved, bd] = [m, d];
        }
        if (!saved || w.time - this.lastSaved < 60) return;
        this.lastSaved = w.time;
        return this.rumour('saved', saved.def.name, mon.x, mon.y);
      }
      case 'quest':
        if (ev.status === 'completed') this.rumour('quest', '', p.x, p.y);
        return;
      case 'ladder':
        if (ev.phase === 'result' && ev.won) this.rumour('duelWon', '', p.x, p.y);
        return;
      case 'bossDefeated':
        return this.rumour('boss', '', p.x, p.y);
      case 'karma':
        if (ev.id === p.id && (ev.karma === 'evil' || ev.karma === 'mad')) this.rumour('pk', '', p.x, p.y);
        return;
      case 'npcGift': {
        const m = this.byEntity.get(ev.id);
        if (!m) return;
        const thanks = m.e ? this.voice(m, m.e, 'gift') : null;
        if (thanks && m.e) this.bark(m.e, thanks);
        this.rumour('gift', m.def.name, m.x, m.y);
        return;
      }
    }
  }

  // ───────────────────────── helpers ─────────────────────────

  private bark(e: Entity, text: string): void {
    const pr = this.h.world.progress;
    this.h.emit({ type: 'bark', id: e.id, text: text.replaceAll('{name}', pr.name) });
  }

  private pick<T>(xs: readonly T[]): T {
    return xs[Math.floor(this.rnd() * xs.length)]!;
  }

  /** One of the people (out of doors) standing at a ground point (the client's click pick). */
  personAt(x: number, y: number): Entity | undefined {
    let best: Entity | undefined;
    let bd = 1.2;
    for (const m of this.minds) {
      if (!m.e) continue;
      // A figure stands up the screen from its feet: the point may be a little north of them.
      const d = Math.hypot(m.e.x - x, m.e.y - y - 0.3);
      if (d < bd) [best, bd] = [m.e, d];
    }
    return best;
  }

  /** Picking one of the people out, or speaking to them (the World only takes its own townsfolk for these). */
  refuse(cmd: PlayerCommand): boolean {
    if (cmd.t !== 'select' && cmd.t !== 'interact') return false;
    const w = this.h.world;
    const e = w.get(cmd.id);
    const m = e ? this.byEntity.get(e.id) : undefined;
    if (!e || !m || w.player.dead) return false;
    w.player.targetId = e.id;
    if (cmd.t === 'interact' && Math.hypot(e.x - w.player.x, e.y - w.player.y) < 4) {
      this.h.face(e, w.player);
      const tier = this.tierOf(m);
      const l = (tier > 0 && this.rnd() < 0.6 ? this.tierLine(m, tier) : null) ?? this.voice(m, e, 'greet');
      if (l) this.bark(e, l);
    }
    return true;
  }

  /** The mind behind an entity, if it is one of this system's people. */
  mindOf(e: Entity): LifeMind | undefined {
    return this.byEntity.get(e.id);
  }

  /** A person's mind by id (data/npcLife.ts `LIFE_PEOPLE`). */
  mind(id: string): LifeMind | undefined {
    return this.byId.get(id);
  }

  /** The spots of a place on this map (tests, tools). */
  spotsOf(place: LifePlace): readonly LifeSpot[] {
    return this.spots.get(place) ?? [];
  }
}
